import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import type { BulletinSettings, BulletinTemplate } from '@fellowship/shared'
import { useChurch, useChurchEvents } from '../hooks/useChurches'
import { useUpdateChurch } from '../hooks/useChurchAdmin'
import { AdminShell } from '../components/admin/AdminShell'
import { AdminPageHeader, AdminCard, AdmBtn } from '../components/admin/AdminAtoms'
import { AdmIcn } from '../components/admin/AdminIcons'
import { BULLETIN_TEMPLATES, TEMPLATE_ORDER } from '../components/bulletin/BulletinTemplates'

const DEFAULT_VERSE = '"Come, let us bow down in worship, let us kneel before the Lord our Maker." — Psalm 95:6'

const DEFAULT_SETTINGS: BulletinSettings = {
  template: 'classic',
  showLogo: true,
  showServiceTimes: true,
  showEvents: true,
  showGiving: true,
  showVerse: true,
  maxEvents: 4,
  verse: DEFAULT_VERSE,
}

export default function BulletinPage() {
  const { slug } = useParams<{ slug: string }>()
  const { data: churchData, isLoading: churchLoading } = useChurch(slug)
  const { data: eventsData, isLoading: eventsLoading } = useChurchEvents(slug)

  const church = churchData?.data
  const updateChurch = useUpdateChurch(church?.id ?? '')

  const [settings, setSettings] = useState<BulletinSettings | null>(null)
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle')

  // Hydrate settings from the saved church config exactly once per church.
  const hydratedChurchId = useRef<string | null>(null)
  const savedJson = useRef<string>('')
  useEffect(() => {
    if (church && hydratedChurchId.current !== church.id) {
      const initial = church.bulletinSettings ?? DEFAULT_SETTINGS
      setSettings(initial)
      savedJson.current = JSON.stringify(initial)
      hydratedChurchId.current = church.id
    }
  }, [church])

  // Debounced autosave so multiple coordinators share one config.
  useEffect(() => {
    if (!settings || !church?.id) return
    const json = JSON.stringify(settings)
    if (json === savedJson.current) return
    setSaveState('saving')
    const t = setTimeout(() => {
      updateChurch.mutate(
        { bulletinSettings: settings },
        {
          onSuccess: () => {
            savedJson.current = json
            setSaveState('saved')
          },
          onError: () => setSaveState('idle'),
        },
      )
    }, 700)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, church?.id])

  const profileUrl = slug ? `${window.location.origin}/churches/${slug}` : ''
  useEffect(() => {
    if (!profileUrl) return
    QRCode.toDataURL(profileUrl, { width: 240, margin: 1, color: { dark: '#1a1a1a', light: '#ffffff' } })
      .then(setQrDataUrl)
      .catch(() => {})
  }, [profileUrl])

  const update = (patch: Partial<BulletinSettings>) => setSettings((s) => (s ? { ...s, ...patch } : s))

  const allOccurrences = (eventsData?.data ?? [])
    .filter((o) => !o.isCancelled)
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())

  const isLoading = churchLoading || eventsLoading
  const template: BulletinTemplate = settings?.template ?? 'classic'
  const pageSize = BULLETIN_TEMPLATES[template].pageSize
  const Template = BULLETIN_TEMPLATES[template].Component
  const occurrences = settings ? allOccurrences.slice(0, settings.maxEvents) : []

  return (
    <AdminShell>
      <style>{`
        @media print {
          .bulletin-no-print { display: none !important; }
          aside { display: none !important; }
          body { background: white !important; }
          @page { size: ${pageSize}; margin: 0; }
          main { padding: 0 !important; }
          .bulletin-layout { display: block !important; }
          .bulletin-preview { display: block !important; overflow: visible !important; }
          .bulletin-paper {
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
          }
        }
      `}</style>

      <div className="bulletin-no-print">
        <AdminPageHeader
          eyebrow="Bulletin insert"
          title="Bulletin"
          titleItalic="insert."
          sub="A printable insert for your welcome table or announcements."
          actions={
            <div className="flex items-center gap-3">
              {saveState !== 'idle' && (
                <span className="text-[12px] text-muted">{saveState === 'saving' ? 'Saving…' : 'Saved'}</span>
              )}
              <AdmBtn variant="primary" icon="download" onClick={() => window.print()}>
                Print / Save as PDF
              </AdmBtn>
            </div>
          }
        />
      </div>

      {isLoading || !settings ? (
        <div className="text-[13px] text-muted mt-4">Loading…</div>
      ) : (
        <div className="bulletin-layout flex gap-6 items-start">
          {/* ── Settings panel ───────────────────────────────────────────── */}
          <div className="bulletin-no-print w-[260px] shrink-0 flex flex-col gap-4">
            <AdminCard title="Design">
              <div className="flex flex-col gap-2">
                {TEMPLATE_ORDER.map((key) => {
                  const meta = BULLETIN_TEMPLATES[key]
                  const active = settings.template === key
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => update({ template: key })}
                      className={`text-left rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                        active ? 'border-primary bg-primary/5' : 'border-line-soft bg-surface hover:border-line'
                      }`}
                    >
                      <div className={`text-[13px] font-semibold ${active ? 'text-primary' : 'text-ink-2'}`}>{meta.label}</div>
                      <div className="text-[11.5px] text-muted mt-0.5">{meta.blurb}</div>
                    </button>
                  )
                })}
              </div>
            </AdminCard>

            <AdminCard title="Sections">
              <div className="flex flex-col gap-2.5">
                {([
                  { key: 'showLogo', label: 'Church logo' },
                  { key: 'showServiceTimes', label: 'Service times' },
                  { key: 'showEvents', label: 'Upcoming events' },
                  { key: 'showGiving', label: 'Giving link' },
                  { key: 'showVerse', label: 'Scripture verse' },
                ] as const).map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={settings[key]}
                      onChange={(e) => update({ [key]: e.target.checked } as Partial<BulletinSettings>)}
                      className="w-[15px] h-[15px] rounded cursor-pointer accent-primary"
                    />
                    <span className="text-[13px] text-ink-2">{label}</span>
                  </label>
                ))}
              </div>
            </AdminCard>

            {settings.showEvents && (
              <AdminCard title="Events to show">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => update({ maxEvents: Math.max(1, settings.maxEvents - 1) })}
                    className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                  >
                    <AdmIcn name="minus" size={13} />
                  </button>
                  <span className="text-[14px] font-semibold w-[24px] text-center">{settings.maxEvents}</span>
                  <button
                    type="button"
                    onClick={() => update({ maxEvents: Math.min(8, settings.maxEvents + 1) })}
                    className="w-[28px] h-[28px] rounded-full border border-line-soft bg-surface flex items-center justify-center cursor-pointer text-ink-2"
                  >
                    <AdmIcn name="plus" size={13} />
                  </button>
                  <span className="text-[12px] text-muted ml-1">of {allOccurrences.length}</span>
                </div>
              </AdminCard>
            )}

            {settings.showVerse && (
              <AdminCard title="Scripture verse">
                <textarea
                  value={settings.verse}
                  onChange={(e) => update({ verse: e.target.value })}
                  rows={4}
                  placeholder="Enter a verse…"
                  className="w-full text-[12.5px] text-ink-2 resize-none border border-line-soft rounded-lg px-3 py-2 bg-bg outline-none focus:border-primary transition-colors leading-[1.5] box-border"
                />
                <button
                  type="button"
                  onClick={() => update({ verse: DEFAULT_VERSE })}
                  className="mt-1.5 text-[11.5px] text-muted bg-transparent border-0 cursor-pointer underline underline-offset-2 p-0"
                >
                  Reset to default
                </button>
              </AdminCard>
            )}

            <AdminCard title="Service times">
              {(church?.serviceTimes?.length ?? 0) === 0 ? (
                <div className="text-[12px] text-muted">
                  No service times set.{' '}
                  <a href={`/churches/${slug}/edit`} className="text-primary underline underline-offset-2">
                    Add them in Church profile.
                  </a>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {church?.serviceTimes.map((st, i) => (
                    <div key={i} className="text-[12.5px] text-ink-2">
                      <span className="font-semibold">{st.name}</span>
                      {st.day ? ` · ${st.day}` : ''}
                      {st.time ? ` · ${st.time}` : ''}
                    </div>
                  ))}
                </div>
              )}
            </AdminCard>
          </div>

          {/* ── Live preview ─────────────────────────────────────────────── */}
          <div className="bulletin-preview flex-1 flex justify-center overflow-auto">
            {church && (
              <Template
                church={church}
                occurrences={occurrences}
                qrDataUrl={qrDataUrl}
                profileUrl={profileUrl}
                settings={settings}
              />
            )}
          </div>
        </div>
      )}
    </AdminShell>
  )
}
